/** Persisted chart UI preferences (timeframe, type, colors) shared by TvChart and menu modals. */

export const CHART_PREF_EVENT = "fx-chart-prefs-changed";
const STORAGE_KEY = "fxbazzar_chart_prefs";

export const TIMEFRAME_OPTIONS = [
  { id: "1m", label: "1m" },
  { id: "5m", label: "5m" },
  { id: "15m", label: "15m" },
  { id: "1h", label: "1h" },
  { id: "1d", label: "1D" },
];

export const CHART_TYPE_OPTIONS = [
  { id: "candle", label: "Candles" },
  { id: "ohlc", label: "Bars" },
  { id: "hollow", label: "Hollow candles" },
  { id: "volcandle", label: "Volume candles" },
  { id: "line", label: "Line" },
  { id: "linemarkers", label: "Line with markers" },
  { id: "stepline", label: "Step line" },
  { id: "area", label: "Area" },
  { id: "hlcarea", label: "HLC area" },
  { id: "baseline", label: "Baseline" },
  { id: "column", label: "Columns" },
  { id: "hilo", label: "High-low" },
];

/** MT5-style color templates (mobile Settings → Color Templates). */
export const COLOR_TEMPLATES = [
  {
    id: "default",
    label: "Green & Red",
    shortLabel: "Green & Red",
    labelKey: "colorTplGreenRed",
    upColor: "#4caf50",
    downColor: "#f44336",
  },
  {
    id: "blue-red",
    label: "Blue & Red",
    shortLabel: "Blue & Red",
    labelKey: "colorTplBlueRed",
    upColor: "#2196f3",
    downColor: "#f44336",
  },
  {
    id: "black-white",
    label: "Black & White",
    shortLabel: "Black & White",
    labelKey: "colorTplBlackWhite",
    upColor: "#212121",
    downColor: "#bdbdbd",
  },
  {
    id: "neutral",
    label: "Neutral",
    shortLabel: "Neutral",
    labelKey: "colorTplNeutral",
    upColor: "#a5d6a7",
    downColor: "#ef9a9a",
  },
];

/** Map legacy template ids so older saved prefs still resolve cleanly. */
const LEGACY_COLOR_TEMPLATE_IDS = {
  classic: "default",
  "blue-orange": "blue-red",
  forest: "default",
  mono: "neutral",
};

export const DEFAULT_CHART_PREFS = {
  defaultTimeframe: "1m",
  defaultChartType: "candle",
  showVolume: true,
  crosshairMagnet: true,
  colorTemplateId: "default",
};

export function getColorTemplate(id) {
  const resolved = LEGACY_COLOR_TEMPLATE_IDS[id] || id;
  return COLOR_TEMPLATES.find((t) => t.id === resolved) || COLOR_TEMPLATES[0];
}

/**
 * TradingView series color overrides for a color template.
 * Applied on chart init and whenever the user picks a template in the menu.
 */
export function getColorTemplateOverrides(templateId) {
  const { upColor, downColor } = getColorTemplate(templateId);
  return {
    "mainSeriesProperties.candleStyle.upColor": upColor,
    "mainSeriesProperties.candleStyle.downColor": downColor,
    "mainSeriesProperties.candleStyle.wickUpColor": upColor,
    "mainSeriesProperties.candleStyle.wickDownColor": downColor,
    "mainSeriesProperties.candleStyle.borderUpColor": upColor,
    "mainSeriesProperties.candleStyle.borderDownColor": downColor,

    "mainSeriesProperties.hollowCandleStyle.upColor": upColor,
    "mainSeriesProperties.hollowCandleStyle.downColor": downColor,
    "mainSeriesProperties.hollowCandleStyle.wickUpColor": upColor,
    "mainSeriesProperties.hollowCandleStyle.wickDownColor": downColor,
    "mainSeriesProperties.hollowCandleStyle.borderUpColor": upColor,
    "mainSeriesProperties.hollowCandleStyle.borderDownColor": downColor,

    "mainSeriesProperties.barStyle.upColor": upColor,
    "mainSeriesProperties.barStyle.downColor": downColor,

    "mainSeriesProperties.columnStyle.upColor": upColor,
    "mainSeriesProperties.columnStyle.downColor": downColor,

    "mainSeriesProperties.haStyle.upColor": upColor,
    "mainSeriesProperties.haStyle.downColor": downColor,
    "mainSeriesProperties.haStyle.wickUpColor": upColor,
    "mainSeriesProperties.haStyle.wickDownColor": downColor,
    "mainSeriesProperties.haStyle.borderUpColor": upColor,
    "mainSeriesProperties.haStyle.borderDownColor": downColor,
  };
}

export function loadChartPreferences() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CHART_PREFS };
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_CHART_PREFS, ...parsed };
    return {
      ...merged,
      defaultTimeframe: TIMEFRAME_OPTIONS.some((t) => t.id === merged.defaultTimeframe)
        ? merged.defaultTimeframe
        : DEFAULT_CHART_PREFS.defaultTimeframe,
      // Always coerce to a known type — default is Candles (TradingView ChartStyle = 1).
      defaultChartType: CHART_TYPE_OPTIONS.some((t) => t.id === merged.defaultChartType)
        ? merged.defaultChartType
        : DEFAULT_CHART_PREFS.defaultChartType,
      colorTemplateId: getColorTemplate(merged.colorTemplateId).id,
    };
  } catch {
    return { ...DEFAULT_CHART_PREFS };
  }
}

export function saveChartPreferences(partial, { broadcast = true } = {}) {
  const current = loadChartPreferences();
  const merged = { ...current, ...partial };
  const next = {
    ...merged,
    defaultTimeframe: TIMEFRAME_OPTIONS.some((t) => t.id === merged.defaultTimeframe)
      ? merged.defaultTimeframe
      : DEFAULT_CHART_PREFS.defaultTimeframe,
    defaultChartType: CHART_TYPE_OPTIONS.some((t) => t.id === merged.defaultChartType)
      ? merged.defaultChartType
      : DEFAULT_CHART_PREFS.defaultChartType,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  if (broadcast && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHART_PREF_EVENT, { detail: next }));
  }
  return next;
}
